import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  StatusDot,
  Text,
} from "@teals/ui-web";
import {
  useDeleteApiIntegrationMutation,
  useGetApiIntegrationQuery,
  useUpdateApiAgentAssociationsMutation,
  useUpdateApiIntegrationMutation,
  useVerifyApiIntegrationMutation,
  apiHost,
} from "../../store/api/apiIntegrationsApi";
import { useGetAgentsQuery } from "../../store/api/agentsApi";
import { useAuth } from "../../hooks/useAuth";
import { formatRelativeTime } from "../DataSources/relativeTime";
import {
  AssociationItem,
  AssociationName,
  DetailField,
  DetailFieldLabel,
  DetailFieldValue,
  DetailSection,
  DetailSectionTitle,
  PageWrapper,
} from "../DataSources/styles";
import {
  ComingSoonLayout,
  ConnectionStrip,
  Crumb,
  InstanceActions,
  InstanceHeader,
  InstanceTitleRow,
  StripItem,
} from "./styles";
import { INTEGRATIONS_HUB } from "./paths";

const ApiIntegrationInstance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId || "";
  const { data: api, isLoading } = useGetApiIntegrationQuery(id || "", {
    skip: !id,
  });
  const { data: agents = [] } = useGetAgentsQuery({ orgId }, { skip: !orgId });
  const [updateApi] = useUpdateApiIntegrationMutation();
  const [verifyApi, verifyState] = useVerifyApiIntegrationMutation();
  const [updateAssociations] = useUpdateApiAgentAssociationsMutation();
  const [deleteApi] = useDeleteApiIntegrationMutation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [ownKeyOpen, setOwnKeyOpen] = useState(false);
  const [ownKey, setOwnKey] = useState("");
  const [addAgentId, setAddAgentId] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const agentNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const agent of agents) {
      if (agent.id) names[agent.id] = agent.name || agent.agentName;
    }
    return names;
  }, [agents]);

  const availableAgents = useMemo(() => {
    const linked = new Set(api?.agentIds || []);
    return agents.filter((agent) => agent.id && !linked.has(agent.id));
  }, [agents, api?.agentIds]);

  if (isLoading || !api) {
    return (
      <PageWrapper>
        <EmptyState title={isLoading ? "Loading API…" : "API not found."} />
      </PageWrapper>
    );
  }

  const placement = api.auth?.placements?.[0];
  const keyLabel =
    api.credentialSource === "org" ? "Your key" : "Shared demo key";
  const secret = api.auth?.secrets?.[0];

  const onVerify = async () => {
    setError(null);
    try {
      const result = await verifyApi({ id: api.apiId }).unwrap();
      setNotice(result.message || (result.ok ? "Verified." : "Verification failed."));
    } catch {
      setError("Verification failed. The key was not returned.");
    }
  };

  const onSaveOwnKey = async () => {
    const value = ownKey.trim();
    if (!value || !secret) return;
    setError(null);
    try {
      await updateApi({
        id: api.apiId,
        body: { credentials: { [secret.id]: value } },
      }).unwrap();
      setOwnKey("");
      setOwnKeyOpen(false);
      setNotice("Saved your key. It will not be shown again.");
    } catch {
      setError("Could not store the key.");
    }
  };

  const onUseShared = async () => {
    setError(null);
    try {
      await updateApi({
        id: api.apiId,
        body: { use_platform_key: true },
      }).unwrap();
      setNotice("This organisation is using the shared demo key again.");
    } catch {
      setError("Could not switch back to the shared key.");
    }
  };

  return (
    <PageWrapper>
      <Crumb>
        <Link to={INTEGRATIONS_HUB}>Integrations</Link>
        <span> / {api.name}</span>
      </Crumb>
      <InstanceHeader>
        <div>
          <InstanceTitleRow>
            <Text variant="h2">{api.name}</Text>
            <Badge variant="info" size="sm">
              API
            </Badge>
            <Badge
              variant={api.status === "active" ? "success" : "neutral"}
              size="sm"
            >
              <StatusDot
                status={api.status === "active" ? "active" : "draft"}
                size="sm"
              />
              {api.status === "active" ? "Active" : "Key missing"}
            </Badge>
            <Badge variant="neutral" size="sm">
              {keyLabel}
            </Badge>
          </InstanceTitleRow>
          <Text variant="body2">
            {api.description ||
              "HTTP API agents can call. The key is never shown again."}
          </Text>
        </div>
        <InstanceActions>
          <Button
            variant="secondary"
            size="sm"
            onClick={onVerify}
            disabled={verifyState.isLoading}
          >
            Verify
          </Button>
          <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
            Delete
          </Button>
        </InstanceActions>
      </InstanceHeader>

      <ConnectionStrip>
        <StripItem>
          <label>API ID</label>
          <span>{api.apiId}</span>
        </StripItem>
        <StripItem>
          <label>Host</label>
          <span>{api.host || apiHost(api.baseUrl)}</span>
        </StripItem>
        <StripItem>
          <label>Key sent as</label>
          <span>
            {placement ? `${placement.in} ${placement.name}` : "Not set"}
          </span>
        </StripItem>
        <StripItem>
          <label>Last verified</label>
          <span>
            {api.lastVerifiedAt
              ? formatRelativeTime(api.lastVerifiedAt)
              : "Not yet"}
          </span>
        </StripItem>
        <StripItem>
          <label>Organisation</label>
          <span>{api.orgName || "This organisation"}</span>
        </StripItem>
      </ConnectionStrip>

      {notice && <Text variant="body2">{notice}</Text>}
      {error && <Text variant="body2">{error}</Text>}

      <ComingSoonLayout>
        <div>
          <DetailSection>
            <DetailSectionTitle>Where the key is sent</DetailSectionTitle>
            {(api.auth?.secrets || []).map((item) => (
              <DetailField key={item.id}>
                <DetailFieldLabel>{item.label}</DetailFieldLabel>
                <DetailFieldValue>
                  {item.configured ? "Configured" : "Not configured"}
                </DetailFieldValue>
              </DetailField>
            ))}
            {(api.auth?.placements || []).map((item) => (
              <DetailField key={`${item.in}-${item.name}`}>
                <DetailFieldLabel>Placement</DetailFieldLabel>
                <DetailFieldValue>
                  {item.in} · {item.name}
                  {item.prefix ? ` · prefix “${item.prefix}”` : ""}
                </DetailFieldValue>
              </DetailField>
            ))}
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOwnKeyOpen(true)}
              >
                Use your own key
              </Button>
              {api.credentialSource === "org" && (
                <Button variant="ghost" size="sm" onClick={onUseShared}>
                  Use shared key
                </Button>
              )}
            </div>
          </DetailSection>

          <DetailSection>
            <DetailSectionTitle>Operations</DetailSectionTitle>
            {(api.operations || []).length === 0 ? (
              <EmptyState title="No operations defined." />
            ) : (
              api.operations.map((operation) => (
                <DetailField key={operation.id}>
                  <DetailFieldLabel>{operation.id}</DetailFieldLabel>
                  <DetailFieldValue>
                    {operation.description || operation.path}
                  </DetailFieldValue>
                </DetailField>
              ))
            )}
          </DetailSection>
        </div>

        <DetailSection>
          <DetailSectionTitle>Agents</DetailSectionTitle>
          {(api.agentIds || []).length === 0 ? (
            <EmptyState title="No agents use this API yet." />
          ) : (
            api.agentIds.map((agentId) => (
              <AssociationItem key={agentId}>
                <AssociationName>
                  {agentNames[agentId] || agentId}
                </AssociationName>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    updateAssociations({
                      id: api.apiId,
                      agentIds: api.agentIds.filter((item) => item !== agentId),
                    })
                  }
                >
                  Remove
                </Button>
              </AssociationItem>
            ))
          )}
          {availableAgents.length > 0 && (
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
              <select
                aria-label="Add agent"
                value={addAgentId}
                onChange={(event) => setAddAgentId(event.target.value)}
              >
                <option value="">Associate an agent…</option>
                {availableAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name || agent.agentName}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                disabled={!addAgentId}
                onClick={async () => {
                  await updateAssociations({
                    id: api.apiId,
                    agentIds: [...(api.agentIds || []), addAgentId],
                  }).unwrap();
                  setAddAgentId("");
                }}
              >
                Add
              </Button>
            </div>
          )}
        </DetailSection>
      </ComingSoonLayout>

      <Modal
        isOpen={ownKeyOpen}
        onClose={() => {
          setOwnKey("");
          setOwnKeyOpen(false);
        }}
        title="Use your own key"
      >
        <Text variant="body1">
          This replaces the shared key for this organisation only. You will not
          be able to see it again.
        </Text>
        <Input
          label={secret?.label || "API key"}
          type="password"
          value={ownKey}
          autoComplete="off"
          onChange={(event) => setOwnKey(event.target.value)}
        />
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={() => setOwnKeyOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSaveOwnKey} disabled={!ownKey.trim()}>
            Save key
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={`Delete ${api.name}?`}
      >
        <Text variant="body1">
          Agents will no longer be able to call this API. The shared demo key
          is not deleted.
        </Text>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              await deleteApi({ id: api.apiId, confirm: true }).unwrap();
              navigate(INTEGRATIONS_HUB);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </PageWrapper>
  );
};

export default ApiIntegrationInstance;
